import React, { useState, useEffect } from 'react';
import { storiesApi } from '@/api/stories';
import { charactersApi } from '@/api/characters';
import { preferencesApi } from '@/api/preferences';
import { Story, Character, UserPreferences, CreateStoryForm, CreateCharacterForm } from '@/types/database';

const TestAPI: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any>({});
  const [error, setError] = useState<string>('');

  // Test data
  const testStory: CreateStoryForm = {
    title: "Test Story - The Magic Adventure",
    description: "A test story to verify our API works",
    genre: "adventure",
    age_group: "3-5",
    illustration_style: "watercolor",
    story_length: "medium",
    main_character: "Test Character",
    prompt: "A magical adventure in a forest"
  };

  const testCharacter: CreateCharacterForm = {
    name: "Test Character",
    type: "human",
    personality: "Brave and curious",
    appearance: "A young child with bright eyes",
    age: "child",
    favorite_color: "blue",
    special_powers: "Can talk to animals"
  };

  const testPreferences = {
    story_length_preference: "medium" as const,
    audience_rating: "3-5" as const,
    preferred_genres: ["adventure", "fantasy"] as const,
    illustration_style_preference: "watercolor" as const
  };

  const runTest = async (testName: string, testFunction: () => Promise<any>) => {
    setLoading(true);
    setError('');
    try {
      const result = await testFunction();
      setResults(prev => ({ ...prev, [testName]: result }));
      console.log(`${testName} result:`, result);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(`${testName} failed: ${errorMsg}`);
      console.error(`${testName} error:`, err);
    } finally {
      setLoading(false);
    }
  };

  const testStoriesAPI = async () => {
    console.log('Testing Stories API...');
    
    // Test 1: Get user usage
    await runTest('getUserUsage', () => storiesApi.getUserUsage());
    
    // Test 2: Create a story
    await runTest('createStory', () => storiesApi.createStory(testStory));
    
    // Test 3: Get user stories
    await runTest('getUserStories', () => storiesApi.getUserStories());
    
    // Test 4: Generate story (placeholder)
    await runTest('generateStory', () => storiesApi.generateStory("A magical adventure", { illustration_style: "watercolor" }));
  };

  const testCharactersAPI = async () => {
    console.log('Testing Characters API...');
    
    // Test 1: Create a character
    await runTest('createCharacter', () => charactersApi.createCharacter(testCharacter));
    
    // Test 2: Get user characters
    await runTest('getUserCharacters', () => charactersApi.getUserCharacters());
    
    // Test 3: Get characters by type
    await runTest('getCharactersByType', () => charactersApi.getCharactersByType('human'));
    
    // Test 4: Get favorite characters
    await runTest('getFavoriteCharacters', () => charactersApi.getFavoriteCharacters());
  };

  const testPreferencesAPI = async () => {
    console.log('Testing Preferences API...');
    
    // Test 1: Get or create preferences
    await runTest('getOrCreatePreferences', () => preferencesApi.getOrCreatePreferences());
    
    // Test 2: Update preferences
    await runTest('updatePreferences', () => preferencesApi.updateUserPreferences(testPreferences));
    
    // Test 3: Get user preferences
    await runTest('getUserPreferences', () => preferencesApi.getUserPreferences());
  };

  const testAllAPIs = async () => {
    console.log('Testing all APIs...');
    await testStoriesAPI();
    await testCharactersAPI();
    await testPreferencesAPI();
  };

  const clearResults = () => {
    setResults({});
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">API Testing Dashboard</h1>
          <p className="text-gray-600 mb-6">
            Test all the API functions to ensure they work with the new storybook schema.
          </p>

          {/* Test Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={testAllAPIs}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium"
            >
              {loading ? 'Testing...' : 'Test All APIs'}
            </button>
            
            <button
              onClick={testStoriesAPI}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium"
            >
              Test Stories API
            </button>
            
            <button
              onClick={testCharactersAPI}
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white px-4 py-2 rounded-lg font-medium"
            >
              Test Characters API
            </button>
            
            <button
              onClick={testPreferencesAPI}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-4 py-2 rounded-lg font-medium"
            >
              Test Preferences API
            </button>
          </div>

          <button
            onClick={clearResults}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            Clear Results
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-red-800 font-medium">Error</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Results Display */}
        <div className="space-y-6">
          {Object.entries(results).map(([testName, result]) => (
            <div key={testName} className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3 capitalize">
                {testName.replace(/([A-Z])/g, ' $1').trim()}
              </h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Result:</h4>
                <pre className="text-sm text-gray-800 overflow-x-auto">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            </div>
          ))}
        </div>

        {/* Test Data Display */}
        <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Test Data</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Test Story:</h4>
              <pre className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                {JSON.stringify(testStory, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Test Character:</h4>
              <pre className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                {JSON.stringify(testCharacter, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Test Preferences:</h4>
              <pre className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                {JSON.stringify(testPreferences, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestAPI; 